// =============================================================================
// MATCHING UNDERGARMENT (v2.1.3) — pseudo-option picker for separate UG product
// -----------------------------------------------------------------------------
// Scope: PDP feature where dress (Color + Size native variants) can be paired
// with a matching undergarment from a SEPARATE Shopify product. UG product
// itself has Color + Size variants (Size labels match dress 1:1, e.g. 0-4).
// Selected UG added as a 2nd line item with $0 price, linked via line-item
// properties (_linked_dress_variant_id + _addon_type=matching_undergarment).
//
// State model:
//   { dressColor, dressSize, selectedUgColor, resolvedVariantId (derived) }
//   Note: selection is UG Color only; the actual variant ID is resolved at
//   submit time from (selectedUgColor × current dressSize).
//
// Behavior:
//   - Mapping data island [data-ug-mapping] keyed by dress Color → flat list
//     of allowed UG variants (each with ug_color + ug_size + variant_id).
//   - Always-selected model: dress products with UG mapping always have a
//     UG selected (no deselect). Init + dress Color change → auto-select
//     first in-stock UG in the visible group (fallback first OOS if all OOS).
//   - On dress Color change: clear selection, recompute group visibility +
//     availability, auto-select first available in new group.
//   - On dress Size change: recompute per-color availability (OOS Color
//     gets `.disabled` class, but button STAYS CLICKABLE). Selection
//     persists — if it becomes OOS, applyOosState swaps ATC→BIS like a
//     native OOS variant pick (mirrors custom.js:336 toggleButtons).
//   - Color button click: switch selection (no deselect). Works on OOS
//     buttons too — selection drives gallery preview + ATC↔BIS swap.
//   - Form submit: if combo resolvable + available, AJAX POST 2-item
//     /cart/add.js. Else silently abort (UI already in BIS state, ATC was
//     not meant to be clickable).
//   - Gallery 2-axis filter: piggyback on GalleryController in style-with.js
//     via window.__pseudoUgValue (set to UG color label) + 'ug:change' event.
//
// Source of truth for mapping: product.metafields.custom.ug_mapping (list
// metaobject_reference → dress_ug_color_mapping). See CUSTOMIZATIONS.md for
// metaobject schema.
// =============================================================================
(function MatchingUndergarment() {
  const FORM_SEL = 'form.product-single__form';
  const PICKER_WRAPPER_SEL = '[data-ug-picker-wrapper]';
  const PICKER_SEL = '[data-ug-picker]';
  const MAPPING_SEL = 'script[data-ug-mapping]';

  function getForm() { return document.querySelector(FORM_SEL); }
  function getPickerWrapper() { return document.querySelector(PICKER_WRAPPER_SEL); }
  function getPicker() { return document.querySelector(PICKER_SEL); }

  function readMapping() {
    const node = document.querySelector(MAPPING_SEL);
    if (!node) return null;
    try { return JSON.parse(node.textContent || '{}'); } catch { return null; }
  }

  function getFieldsetByName(targetName) {
    const form = getForm();
    if (!form) return null;
    return Array.from(form.querySelectorAll('fieldset')).find((fs) => {
      const n = (fs.getAttribute('name') || '').toLowerCase();
      return n === targetName;
    }) || null;
  }

  function getCurrentColor() {
    const fs = getFieldsetByName('color') || getFieldsetByName('colour');
    if (!fs) return null;
    const r = fs.querySelector('input[type="radio"]:checked');
    return r ? r.value : null;
  }

  function getCurrentSize() {
    const fs = getFieldsetByName('size');
    if (!fs) return null;
    const r = fs.querySelector('input[type="radio"]:checked');
    return r ? r.value : null;
  }

  function getSelectedSwatch() {
    const picker = getPicker();
    if (!picker) return null;
    return picker.querySelector('.ug-picker__swatch[aria-pressed="true"]');
  }

  function getSelected() {
    const sw = getSelectedSwatch();
    if (!sw) return { color: null, label: null };
    return {
      color: sw.getAttribute('data-ug-color'),
      label: sw.getAttribute('data-ug-label'),
    };
  }

  // Resolve specific UG variant from (dressColor → allowed list, ugColor, dressSize).
  // Returns { variant_id, available, label } or null if no match.
  function resolveUgVariant(mapping, dressColor, ugColor, dressSize) {
    if (!mapping || !dressColor || !ugColor || dressSize == null) return null;
    const entry = mapping[dressColor];
    if (!entry || !Array.isArray(entry.variants)) return null;
    return entry.variants.find((v) =>
      String(v.ug_color) === String(ugColor) &&
      String(v.ug_size) === String(dressSize)
    ) || null;
  }

  function clearSelection() {
    const picker = getPicker();
    if (!picker) return;
    picker.querySelectorAll('.ug-picker__swatch[aria-pressed="true"]').forEach((sw) => {
      sw.setAttribute('aria-pressed', 'false');
    });
    const info = document.querySelector('[data-ug-selected-info]');
    const lbl = document.querySelector('[data-ug-selected-label]');
    if (info) info.hidden = true;
    if (lbl) lbl.textContent = '';
    window.__pseudoUgValue = null;
  }

  function selectSwatch(sw) {
    const picker = getPicker();
    if (!picker || !sw) return;
    picker.querySelectorAll('.ug-picker__swatch[aria-pressed="true"]').forEach((other) => {
      if (other !== sw) other.setAttribute('aria-pressed', 'false');
    });
    sw.setAttribute('aria-pressed', 'true');
    const label = sw.getAttribute('data-ug-label');
    const info = document.querySelector('[data-ug-selected-info]');
    const lbl = document.querySelector('[data-ug-selected-label]');
    if (info) info.hidden = false;
    if (lbl) lbl.textContent = label;
    window.__pseudoUgValue = label;
  }

  function applyColorFilter() {
    const wrapper = getPickerWrapper();
    const picker = getPicker();
    if (!wrapper || !picker) return;
    const color = getCurrentColor();
    let anyVisible = false;
    picker.querySelectorAll('.ug-picker__group').forEach((group) => {
      const match = group.getAttribute('data-applies-color') === color;
      group.hidden = !match;
      if (match) anyVisible = true;
    });
    wrapper.hidden = !anyVisible;
  }

  // For each Color button in the currently-visible group, check whether the
  // (ugColor × current dressSize) combo exists AND is available. Toggle the
  // native `.disabled` class to mirror native out-of-stock visual. Button
  // stays clickable when OOS — click triggers gallery preview + ATC↔BIS swap
  // (see applyOosState). Selection persists across Size change; if it becomes
  // OOS, applyOosState handles the native ATC→BIS swap.
  function applyAvailability() {
    const picker = getPicker();
    if (!picker) return;
    const mapping = readMapping();
    const dressColor = getCurrentColor();
    const dressSize = getCurrentSize();

    picker.querySelectorAll('.ug-picker__group').forEach((group) => {
      if (group.hidden) return;
      group.querySelectorAll('.ug-picker__swatch').forEach((sw) => {
        const ugColor = sw.getAttribute('data-ug-color');
        const resolved = resolveUgVariant(mapping, dressColor, ugColor, dressSize);
        const available = !!(resolved && resolved.available);

        sw.classList.toggle('disabled', !available);
        const baseLabel = sw.getAttribute('data-ug-label') || ugColor;
        sw.setAttribute('aria-label', baseLabel + (available ? '' : ' — sold out'));
      });
    });
  }

  // Read native dress availability by replicating updateVariantSelection's
  // lookup (custom.js:287-313): match "Size / Color" prefix in master select
  // <option>, return !disabled. True if no match (defensive default).
  function getNativeDressAvailability() {
    const colorRadio = document.querySelector('.product-single__form input[name="Color"]:checked, .product-single__form input[name="Colour"]:checked');
    const sizeRadio = document.querySelector('.product-single__form input[name="Size"]:checked');
    if (!colorRadio || !sizeRadio) return true;
    const searchKey = sizeRadio.value + ' / ' + colorRadio.value;
    const selectEl = document.querySelector('.product-single__form .product-single__variants');
    if (!selectEl) return true;
    for (let i = 0; i < selectEl.options.length; i++) {
      if (selectEl.options[i].textContent.trim().indexOf(searchKey) === 0) {
        return !selectEl.options[i].disabled;
      }
    }
    return true;
  }

  // Single source of truth for the currently-selected UG's resolved variant.
  // Returns { ugColor, resolved } where resolved is {variant_id, available, label}
  // or null. Centralizes lookup so isSelectedUgOos + applyOosState don't re-query.
  function getSelectedUgResolved() {
    const { color: ugColor } = getSelected();
    if (!ugColor) return { ugColor: null, resolved: null };
    const resolved = resolveUgVariant(readMapping(), getCurrentColor(), ugColor, getCurrentSize());
    return { ugColor, resolved };
  }

  function isSelectedUgOos() {
    const { ugColor, resolved } = getSelectedUgResolved();
    if (!ugColor) return false;
    return !resolved || !resolved.available;
  }

  // Mirror native toggleButtons (custom.js:336-378) with combined truth
  // (dress OOS OR selected UG OOS). When BIS button exists → swap ATC↔BIS
  // via `.disabled` class. Else → mutate ATC text to SOLD OUT (native fallback).
  //
  // BIS variant binding (v2.1.3): AMP Back in Stock app reads data-variant-id
  // attribute on #BIS_trigger at click time, overriding the form's native
  // <input name="id"> binding (per help.useamp.com/article/1187 — the
  // documented pattern updates the attr dynamically via selectCallback). When
  // UG OOS triggers the BIS swap, point the waitlist at the resolved UG
  // variant so customer subscribes to the OOS UG, not the in-stock dress.
  // Clear the attr for dress-OOS or in-stock paths so AMP falls back to the
  // native dress variant.
  function applyOosState() {
    const { ugColor, resolved } = getSelectedUgResolved();
    const ugOos = !!ugColor && (!resolved || !resolved.available);
    const showBis = ugOos || !getNativeDressAvailability();
    const atc = document.querySelector('.product-single__form .add-to-cart:not(#BIS_trigger)');
    const bis = document.querySelector('.product-single__form #BIS_trigger');
    if (!atc) return;

    if (bis) {
      atc.classList.toggle('disabled', showBis);
      bis.classList.toggle('disabled', !showBis);

      if (ugOos && resolved && resolved.variant_id) {
        bis.setAttribute('data-variant-id', String(resolved.variant_id));
      } else {
        bis.removeAttribute('data-variant-id');
      }
    } else {
      atc.classList.toggle('disabled', showBis);
      const text = atc.querySelector('span');
      if (text) {
        const def = text.getAttribute('data-default-text') || 'ADD TO CART';
        text.textContent = showBis ? 'SOLD OUT' : def;
      }
    }
  }

  function notifyGalleryRefresh() {
    const form = getForm();
    if (form) form.dispatchEvent(new CustomEvent('ug:change', { bubbles: true }));
  }

  // Auto-select first in-stock UG Color in the currently-visible group.
  // Falls back to the first button if all OOS (so customer always sees a
  // selection + can still preview the OOS style). No-op when no group is
  // visible (e.g. dress Color has no UG mapping → picker hidden).
  function autoSelectFirstAvailable() {
    const picker = getPicker();
    if (!picker) return;
    const visibleGroup = picker.querySelector('.ug-picker__group:not([hidden])');
    if (!visibleGroup) return;
    const buttons = Array.from(visibleGroup.querySelectorAll('.ug-picker__swatch'));
    if (!buttons.length) return;
    const target = buttons.find((sw) => !sw.classList.contains('disabled')) || buttons[0];
    selectSwatch(target);
  }

  function handleSwatchClick(e) {
    const sw = e.target.closest('.ug-picker__swatch');
    if (!sw) return;
    e.preventDefault();
    // No-deselect: dress products with UG always have a UG selected.
    // Clicking the active button is a no-op; clicking a different button
    // (in-stock or OOS) switches the selection.
    if (sw.getAttribute('aria-pressed') === 'true') return;
    selectSwatch(sw);
    applyOosState();
    notifyGalleryRefresh();
  }

  function handleNativeChange(e) {
    const radio = e.target.closest('input[type="radio"]');
    if (!radio) return;
    const fs = radio.closest('fieldset');
    const name = (fs?.getAttribute('name') || '').toLowerCase();
    if (name === 'color' || name === 'colour') {
      // Color change: clear UG state (in case new Color has no mapping →
      // picker hidden, no auto-select fires), recompute group visibility +
      // availability, then auto-select first available UG in the new group.
      // Dress products with UG always have a UG selected.
      clearSelection();
      applyColorFilter();
      applyAvailability();
      autoSelectFirstAvailable();
      notifyGalleryRefresh();
    } else if (name === 'size') {
      // Size change: keep current UG color selection (do not auto-clear).
      // If (UG color × new dress size) is OOS, applyOosState triggers the
      // native ATC↔BIS swap to mirror an OOS variant pick.
      applyAvailability();
      notifyGalleryRefresh();
    }
    applyOosState();
    // Native updateVariantSelection (custom.js:287) defers toggleButtons via
    // waitForButtonAndToggle (100ms poll). Re-apply after that runs so our
    // override (e.g. dress in-stock but selected UG OOS) wins.
    setTimeout(applyOosState, 250);
  }

  async function handleSubmit(e) {
    const form = e.target;
    const { color: ugColor, label: ugLabel } = getSelected();
    if (!ugColor) return; // no UG selected → native submit

    e.preventDefault();
    e.stopImmediatePropagation();

    const dressVariantInput = form.querySelector('[name="id"]');
    if (!dressVariantInput) return;
    const dressVariantId = parseInt(dressVariantInput.value, 10);
    const qtyInput = form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

    // Resolve UG variant: (dress Color → allowed list) × ugColor × dress Size.
    const dressColor = getCurrentColor();
    const dressSize = getCurrentSize();
    const mapping = readMapping();
    const resolved = resolveUgVariant(mapping, dressColor, ugColor, dressSize);

    // Silently abort if combo unresolvable or OOS — UI is already in BIS
    // state (applyOosState swapped ATC→BIS) so ATC click is a no-op edge
    // case (e.g. Enter key on a text input). No inline message — match
    // native OOS variant flow.
    if (!resolved || !resolved.available) return;

    const atcBtn = form.querySelector('.add-to-cart');
    const originalText = atcBtn?.querySelector('span')?.textContent;
    if (atcBtn) atcBtn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: dressVariantId,
              quantity,
              properties: {
                _paired_ug_variant_id: String(resolved.variant_id),
              },
            },
            {
              id: parseInt(resolved.variant_id, 10),
              quantity,
              properties: {
                _linked_dress_variant_id: String(dressVariantId),
                _addon_type: 'matching_undergarment',
              },
            },
          ],
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Cart add failed');
      }

      if (window.jQuery) {
        window.jQuery('body').trigger('added.ajaxProduct');
      } else {
        document.dispatchEvent(new CustomEvent('ajaxProduct.added'));
      }
    } catch (err) {
      console.error('[matching-undergarment]', err);
      showError(err.message || 'Failed to add to cart. Please try again.');
    } finally {
      if (atcBtn) {
        atcBtn.disabled = false;
        const span = atcBtn.querySelector('span');
        if (span && originalText) span.textContent = originalText;
      }
    }
  }

  function showError(msg) {
    let host = document.querySelector('[data-ug-error]');
    if (!host) {
      host = document.createElement('div');
      host.setAttribute('data-ug-error', '');
      host.className = 'ug-picker__error';
      const wrapper = getPickerWrapper();
      if (wrapper && wrapper.parentNode) wrapper.parentNode.insertBefore(host, wrapper.nextSibling);
    }
    host.textContent = msg;
    host.hidden = false;
    setTimeout(() => { if (host) host.hidden = true; }, 4000);
  }

  function init() {
    const form = getForm();
    const picker = getPicker();
    if (!form || !picker) return;

    form.addEventListener('change', handleNativeChange);
    picker.addEventListener('click', handleSwatchClick);
    form.addEventListener('submit', handleSubmit, true);

    applyColorFilter();
    applyAvailability();
    autoSelectFirstAvailable();
    applyOosState();
    notifyGalleryRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
